import type {UUID} from 'src/typings';
import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {FinishedResponseInteractionService} from 'shared/services';
import type {GridCellEditRequest, RecordRow} from 'src/features/record-grid/lib/types';
import type {RecordGridDataService} from 'src/features/record-grid/services/record-grid.data-service';
import {isUndefined} from 'shared/utils/custom-utils';
import {convertFieldTypeIdToFieldId} from 'shared/utils/record-field-utils';
import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
import {getIdFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
import {FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';
import {SUTOR_REPORT_STATES} from 'src/features/erp-sutor/sutor-constants';

export class ReportApprovingService {
    public static serviceName = 'reportsApprovingService';

    /*@ngInject*/
    constructor(private recordGridDataService: RecordGridDataService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private toastMessageService: ToastMessageService) {
    }

    public async getRefreshedReports(reportIdsToRefresh: UUID[]): Promise<RecordRow[]> {
        try {
            return await this.recordGridDataService.loadRows(reportIdsToRefresh);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return [];
        }
    }

    public async approveReport(report: RecordRow, textNote: string | null | undefined): Promise<UUID | null> {
        if (!this.canApproveReport(report)) {
            return null;
        }

        try {
            const stateField = getFirstFieldByFieldTypeIdRecursive(report, FOND_REPORT.stateFieldId);
            const stateValueId = getIdFromGridFieldValue(stateField);

            if (stateValueId !== SUTOR_REPORT_STATES.TO_CONFIRM) {
                return report.id;
            }

            const editRequests: GridCellEditRequest[] = [
                {
                    record: report.id,
                    fieldId: convertFieldTypeIdToFieldId(FOND_REPORT.stateFieldId, [0, 0]),
                    setFieldValueRequest: {value: SUTOR_REPORT_STATES.CONFIRMED},
                    additionalInvalidatingRecords: []
                }
            ];

            if (!isUndefined(textNote)) {
                editRequests.push({
                    record: report.id,
                    fieldId: convertFieldTypeIdToFieldId(FOND_REPORT.textNoteFieldId, [0, 0]),
                    setFieldValueRequest: {value: textNote ?? ''},
                    additionalInvalidatingRecords: []
                });
            }

            await this.recordGridDataService.editCells({cellEditRequests: editRequests});
            this.toastMessageService.showSuccess('Výkaz by schválen!');

            return report.id;
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);

            return null;
        }
    }

    public async disapproveReport(report: RecordRow, textNote: string | null | undefined): Promise<UUID | null> {
        if (!this.canDisapproveReport(report)) {
            return null;
        }

        try {
            const editRequests: GridCellEditRequest[] = [
                {
                    record: report.id,
                    fieldId: convertFieldTypeIdToFieldId(FOND_REPORT.stateFieldId, [0, 0]),
                    setFieldValueRequest: {value: SUTOR_REPORT_STATES.RETURNED},
                    additionalInvalidatingRecords: []
                }
            ];

            if (!isUndefined(textNote)) {
                editRequests.push({
                    record: report.id,
                    fieldId: convertFieldTypeIdToFieldId(FOND_REPORT.textNoteFieldId, [0, 0]),
                    setFieldValueRequest: {value: textNote ?? ''},
                    additionalInvalidatingRecords: []
                });
            }

            await this.recordGridDataService.editCells({cellEditRequests: editRequests});
            this.toastMessageService.showSuccess('Výkaz byl vrácen k dopracování!');

            return report.id;
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return null;
        }
    }

    public async approveReportsBulk(reports: RecordRow[]): Promise<UUID[]> {
        const filteredReports = reports.filter((report) => this.canApproveReport(report));

        const editRequests = filteredReports.map((report) => {
            return {
                record: report.id,
                fieldId: convertFieldTypeIdToFieldId(FOND_REPORT.stateFieldId, [0, 0]),
                setFieldValueRequest: {value: SUTOR_REPORT_STATES.CONFIRMED},
                additionalInvalidatingRecords: []
            };
        });

        try {
            await this.recordGridDataService.editCells({cellEditRequests: editRequests});
            this.toastMessageService.showSuccess('Výkazy byly schváleny!');
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }

        return filteredReports.map((report) => report.id);
    }


    private canApproveReport(report: RecordRow): boolean {
        const stateField = getFirstFieldByFieldTypeIdRecursive(report, FOND_REPORT.stateFieldId);
        const stateValueId = getIdFromGridFieldValue(stateField);
        return stateValueId === SUTOR_REPORT_STATES.TO_CONFIRM;
    }

    private canDisapproveReport(report: RecordRow): boolean {
        const stateField = getFirstFieldByFieldTypeIdRecursive(report, FOND_REPORT.stateFieldId);
        const stateValueId = getIdFromGridFieldValue(stateField);
        return stateValueId === SUTOR_REPORT_STATES.TO_CONFIRM || stateValueId === SUTOR_REPORT_STATES.CONFIRMED;
    }
}